import type { LocalDate } from "@js-joda/core";
import { Api } from "services/api/api.service";
import type {
  AddStaffWorkerTaxRequest,
  StaffProfileData,
} from "services/api/company/staff.service";
import {
  toIsoDateStartOfDayUtc,
  toNonNegativeNumber,
} from "../domains/employment/profilePanel.payload.logic";
import type { EmploymentDetailsForm } from "../profilePanel.contract";

export interface DraftTaxItem {
  id: number;
  startDate: string;
  value: number | null;
}

interface AddTaxDraftItemParams {
  currentEmploymentStartDate: LocalDate | null;
  editedEmploymentDetails: EmploymentDetailsForm;
  effectiveFromDate: LocalDate | null;
  nextDraftId: number;
  setSaveError: (value: string) => void;
  translate: (key: string, options?: Record<string, unknown>) => string;
}

interface DeleteTaxDraftItemParams {
  taxDraftHistory: DraftTaxItem[];
  taxId: number;
}

interface HasTaxDraftHistoryChangesParams {
  taxDraftHistory: DraftTaxItem[];
  taxInitialHistory: DraftTaxItem[];
}

interface SaveTaxDraftHistoryParams {
  companyId: number | null;
  executeSavingAction: (action: () => Promise<void>) => void;
  isSaving: boolean;
  onWorkerUpdated?: () => void;
  profile: StaffProfileData | null;
  reloadProfile: () => void;
  taxDraftHistory: DraftTaxItem[];
  taxInitialHistory: DraftTaxItem[];
  closeModal: () => void;
  setSaveError: (value: string) => void;
  translate: (key: string, options?: Record<string, unknown>) => string;
  workerId: number | null;
}

export function toTaxDraftHistory(
  profile: StaffProfileData | null,
): DraftTaxItem[] {
  return (
    profile?.taxes.map((tax) => ({
      id: tax.id,
      startDate: tax.startDate ?? "",
      value: tax.value,
    })) ?? []
  );
}

export function addTaxDraftItem(
  params: AddTaxDraftItemParams,
): DraftTaxItem | null {
  if (params.effectiveFromDate == null) {
    params.setSaveError(params.translate("Alerts.SomethingWentWrong"));
    return null;
  }

  if (
    params.currentEmploymentStartDate != null &&
    params.effectiveFromDate.isBefore(params.currentEmploymentStartDate)
  ) {
    params.setSaveError(
      params.translate("Staff.ProfilePanelTaxDateBeforeEmploymentStart"),
    );
    return null;
  }

  if (params.effectiveFromDate.dayOfMonth() !== 1) {
    params.setSaveError(
      params.translate("Staff.ProfilePanelTaxDateMustBeFirstOfMonth"),
    );
    return null;
  }

  return {
    id: params.nextDraftId,
    startDate: toIsoDateStartOfDayUtc(params.effectiveFromDate),
    value: toNonNegativeNumber(params.editedEmploymentDetails.taxTable),
  };
}

export function deleteTaxDraftItem(
  params: DeleteTaxDraftItemParams,
): DraftTaxItem[] {
  return params.taxDraftHistory.filter((tax) => tax.id !== params.taxId);
}

export function hasTaxDraftHistoryChanges(
  params: HasTaxDraftHistoryChangesParams,
): boolean {
  return (
    JSON.stringify(params.taxDraftHistory) !==
    JSON.stringify(params.taxInitialHistory)
  );
}

export function saveTaxDraftHistory(params: SaveTaxDraftHistoryParams): void {
  if (params.isSaving) return;
  if (params.companyId == null || params.workerId == null) {
    params.setSaveError(params.translate("Alerts.SomethingWentWrong"));
    return;
  }

  const initialExistingIds = new Set(
    params.taxInitialHistory.filter((tax) => tax.id > 0).map((tax) => tax.id),
  );
  const draftExistingIds = new Set(
    params.taxDraftHistory.filter((tax) => tax.id > 0).map((tax) => tax.id),
  );
  const deletedIds = Array.from(initialExistingIds).filter(
    (id) => !draftExistingIds.has(id),
  );
  const addedItems = params.taxDraftHistory.filter((tax) => tax.id < 0);
  const hasChanges = deletedIds.length > 0 || addedItems.length > 0;

  if (!hasChanges) {
    params.setSaveError("");
    params.closeModal();
    return;
  }

  const companyId = params.companyId;
  const workerId = params.workerId;
  const activeTax =
    params.profile?.taxes.find((tax) => tax.isActive) ??
    params.profile?.taxes[0] ??
    null;

  params.executeSavingAction(async () => {
    for (const taxId of deletedIds) {
      await Api().company.staff.deleteWorkerTax(companyId, workerId, taxId);
    }

    for (const tax of addedItems) {
      const payload: AddStaffWorkerTaxRequest = {
        startDate: tax.startDate,
        taxType: activeTax?.type ?? "Undefined",
        value: tax.value ?? 0,
      };
      await Api().company.staff.addWorkerTax(companyId, workerId, payload);
    }

    params.setSaveError("");
    params.closeModal();
    params.reloadProfile();
    params.onWorkerUpdated?.();
  });
}
